/** Keep decorative alternate photos out of touch-only page loads. */
const templateSelector = 'template[data-gw-hover-media]';
const containerSelector = '[data-gw-hover-container]';
const hoverCapability = matchMedia('(any-hover: hover) and (any-pointer: fine)');
const pending = new Set();

function hydrate(container) {
  observer?.unobserve(container);
  pending.delete(container);
  for (const template of container.querySelectorAll(templateSelector)) {
    template.replaceWith(template.content.cloneNode(true));
  }
}

// Desktop hover photos are ready shortly before their cards enter the viewport.
// Touch-only devices never observe or hydrate them without keyboard focus.
const observer = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && hoverCapability.matches) hydrate(entry.target);
      }
    }, { rootMargin: '160px' })
  : null;

function register(root) {
  if (!(root instanceof Element)) return;
  const templates = root.matches(templateSelector)
    ? [root]
    : root.querySelectorAll(templateSelector);
  for (const template of templates) {
    const container = template.closest(containerSelector);
    if (!container || pending.has(container)) continue;
    pending.add(container);
    if (hoverCapability.matches) observer?.observe(container);
  }
}

function hydrateForInteraction(event) {
  if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
  if (event.type === 'focusin' && !event.target.matches?.(':focus-visible')) return;
  const container = event.target instanceof Element
    ? event.target.closest(containerSelector)
      || event.target.closest('product-card')?.querySelector(containerSelector)
    : null;
  if (container) hydrate(container);
}

// Capture runs before Horizon's pointerenter handler advances the slideshow.
document.addEventListener('pointerover', hydrateForInteraction, true);
document.addEventListener('focusin', hydrateForInteraction, true);
hoverCapability.addEventListener('change', () => {
  observer?.disconnect();
  if (hoverCapability.matches) {
    for (const container of pending) observer?.observe(container);
  }
});

register(document.body);
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) register(node);
    for (const node of record.removedNodes) {
      if (!(node instanceof Element)) continue;
      const containers = node.matches(containerSelector)
        ? [node, ...node.querySelectorAll(containerSelector)]
        : node.querySelectorAll(containerSelector);
      for (const container of containers) {
        if (!container.isConnected) {
          observer?.unobserve(container);
          pending.delete(container);
        }
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
